import React, { useCallback, useState } from 'react';
import {
	Modal, Button, Typography, Space, Divider, Card, Row, Col,
	message,
	notification,
} from 'antd';
import {
	DownloadOutlined, AppleOutlined, WindowsOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { APP_CONFIG } from '../../../config';

const { Title, Text, Paragraph } = Typography;

interface DownloadModelProps {
	open: boolean,
	close: () => void,
	downloadApp: (platform: string) => Promise<void>,
}

export const AppDownloadModal: React.FunctionComponent<DownloadModelProps> = (props) => {
	const { open, close, downloadApp } = props;
	const [downloading, setDownloading] = useState(null);

	const handleDownload = useCallback(async (platform) => {
		setDownloading(platform);
		await downloadApp(platform);
		notification.success({
			message: 'App Download Initiated',
			duration: 2,
			description: 'Downloading the app. Please wait...',
		});
		setDownloading(null);
	}, [downloadApp]);

	const platforms = [
		{
			key: 'win',
			name: 'Windows',
			subtitle: 'Windows 10 or later',
			icon: <WindowsOutlined style={{ fontSize: 20 }} />,
			type: 'default',
		},
		{
			key: 'mac',
			name: 'Mac (Apple Silicon)',
			subtitle: 'M1, M2, M3 chips',
			icon: <AppleOutlined style={{ fontSize: 20 }} />,
			type: 'default',
		},
		{
			key: 'mac-intel',
			name: 'Mac (Intel)',
			subtitle: 'Intel-based Macs',
			icon: <AppleOutlined style={{ fontSize: 20 }} />,
			type: 'default',
		},
	];

	return (
		<Modal
			open={open}
			footer={null}
			closable
			onCancel={close}
			title={null}
			centered
			width={520}
		>
			<div style={{ textAlign: 'center', marginBottom: '24px' }}>
				<div
					style={{
						width: 48,
						height: 48,
						background: 'linear-gradient(135deg, #de6834, #a33403)',
						borderRadius: '16px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						margin: '0 auto 16px',
						boxShadow: '0 4px 16px rgba(24, 144, 255, 0.2)',
					}}
				>
					<DownloadOutlined style={{ fontSize: 24, color: 'white' }} />
				</div>

				<Title level={3} style={{ margin: '0 0 8px', fontWeight: 600 }}>
					Download
					{' '}
					{APP_CONFIG.isChitkara ? 'Testpad' : 'CQ Test'}
					{' '}
					App
				</Title>

				<Paragraph style={{ color: '#666', margin: 0, fontSize: '14px' }}>
					Get the best experience with our native desktop application
				</Paragraph>
			</div>

			<Space direction="vertical" size="middle" style={{ width: '100%' }}>
				{platforms.map((platform) => (
					<Card
						key={platform.key}
						size="small"
						hoverable
						style={{
							borderRadius: '8px',
							position: 'relative',
							overflow: 'hidden',
						}}
						bodyStyle={{ padding: '16px' }}
					>
						<Row align="middle" justify="space-between">
							<Col flex="auto">
								<Space align="center">
									<div
										style={{
											width: 40,
											height: 40,
											background: '#f5f5f5',
											borderRadius: '8px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										{platform.icon}
									</div>

									<div>
										<div style={{ fontWeight: 500, fontSize: '16px' }}>
											{platform.name}
										</div>
										<Text type="secondary" style={{ fontSize: '13px' }}>
											{platform.subtitle}
										</Text>
									</div>
								</Space>
							</Col>

							<Col>
								<Button
									type={platform.type as any}
									size="large"
									loading={downloading === platform.key}
									onClick={() => handleDownload(platform.key)}
									style={{
										borderRadius: '6px',
										fontWeight: 500,
										minWidth: '100px',
									}}
								>
									{downloading === platform.key ? 'Downloading...' : 'Download'}
								</Button>
							</Col>
						</Row>
					</Card>
				))}
			</Space>

			<Divider style={{ margin: '24px 0 16px' }} />

			<div style={{ textAlign: 'center' }}>
				<Space align="start" size="small">
					<InfoCircleOutlined style={{ color: '#de6834', marginTop: '2px' }} />
					<Text type="secondary" style={{ fontSize: '13px', lineHeight: '1.4' }}>
						By downloading, you agree to our Terms of Service and Privacy Policy.
						The app will be automatically updated with new features.
					</Text>
				</Space>
			</div>
		</Modal>
	);
};
